import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Icon } from '@iconify/react';
import bellFill from '@iconify/icons-eva/bell-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import doneAllFill from '@iconify/icons-eva/done-all-fill';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import shieldFill from '@iconify/icons-eva/shield-fill';
import calendarFill from '@iconify/icons-eva/calendar-fill';
import messageCircleFill from '@iconify/icons-eva/message-circle-fill';
import fileTextFill from '@iconify/icons-eva/file-text-fill';
import megaphoneFill from '@iconify/icons-eva/speaker-fill';
import trendingUpFill from '@iconify/icons-eva/trending-up-fill';
import personDoneFill from '@iconify/icons-eva/person-done-fill';
// material
import { alpha } from '@mui/material/styles';
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar
} from '@mui/material';
// components
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import { MIconButton } from '../../components/@material-extend';
import webServices from '../../services/webServices';

// ----------------------------------------------------------------------

// Notification centre (card 161, extended for Service Pro by card 53). The feed is
// served merged by the backend — personal notifications plus any live admin
// broadcast — so this component never has to know which is which. Feed ids are
// opaque strings for the same reason.
//
// ONE component, two audiences. Pass `companyId` and it reads the company's shared
// pro feed instead of the signed-in homeowner's; everything below the fetch is
// identical, which is why this is a prop rather than a second copy of the drawer.
// Read/dismiss on the pro feed apply for the whole team by design (see
// use-cases/proNotifications.js).

const ICON_BY_TYPE = {
  taskReminder: calendarFill,
  warrantyExpiring: shieldFill,
  proMessage: messageCircleFill,
  blogPost: fileTextFill,
  newFeature: megaphoneFill,
  broadcast: megaphoneFill,
  proDigest: trendingUpFill,
  proLowReview: alertTriangleFill,
  proNewLead: personDoneFill
};

const COLOR_BY_LEVEL = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error'
};

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired
};

function NotificationItem({ notification, onSelect }) {
  const color = COLOR_BY_LEVEL[notification.level] || 'info';
  const icon = ICON_BY_TYPE[notification.type] || alertTriangleFill;

  return (
    <ListItem
      button
      disableGutters
      onClick={() => onSelect(notification)}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(notification.isUnRead && { bgcolor: 'action.selected' })
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: (theme) => alpha(theme.palette[color].main, 0.16) }}>
          <Box component={Icon} icon={icon} sx={{ width: 20, height: 20, color: `${color}.main` }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle2">
            {notification.title}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
              &nbsp; {notification.body}
            </Typography>
          </Typography>
        }
        secondary={
          <Typography variant="caption" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
            <Box component={Icon} icon={clockFill} sx={{ mr: 0.5, width: 16, height: 16 }} />
            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
          </Typography>
        }
      />
    </ListItem>
  );
}

NotificationsPopover.propTypes = {
  // When set, the drawer shows this company's shared Service Pro feed (card 53)
  // instead of the signed-in homeowner's personal one.
  companyId: PropTypes.string
};

export default function NotificationsPopover({ companyId }) {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(() => {
    const request = companyId ? webServices.getProNotifications(companyId) : webServices.getNotifications();
    request
      .then((resp) => {
        if (resp && resp.success === true) {
          setNotifications(resp.data.items || []);
          setUnreadCount(resp.data.unreadCount || 0);
        }
      })
      .catch(() => {});
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkAllAsRead = () => {
    // Optimistic: the badge should clear on click, not a round-trip later.
    setNotifications((prev) => prev.map((item) => ({ ...item, isUnRead: false })));
    setUnreadCount(0);
    const request = companyId
      ? webServices.markAllProNotificationsRead(companyId)
      : webServices.markAllNotificationsRead();
    request.catch(load);
  };

  const handleSelect = (notification) => {
    if (notification.isUnRead) {
      setNotifications((prev) =>
        prev.map((item) => (item.id === notification.id ? { ...item, isUnRead: false } : item))
      );
      setUnreadCount((count) => Math.max(0, count - 1));
      const request = companyId
        ? webServices.markProNotificationRead(companyId, notification.id)
        : webServices.markNotificationRead(notification.id);
      request.catch(load);
    }
    setOpen(false);
    if (notification.link) navigate(notification.link);
  };

  const unread = notifications.filter((item) => item.isUnRead);
  const read = notifications.filter((item) => !item.isUnRead);

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={() => setOpen(true)}
        color={open ? 'primary' : 'default'}
        sx={{
          ...(open && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity)
          })
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </MIconButton>

      <MenuPopover open={open} onClose={() => setOpen(false)} anchorEl={anchorRef.current} sx={{ width: 360 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {unreadCount > 0
                ? `You have ${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`
                : 'You are all caught up'}
            </Typography>
          </Box>

          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <MIconButton color="primary" onClick={handleMarkAllAsRead}>
                <Icon icon={doneAllFill} width={20} height={20} />
              </MIconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, maxHeight: 400 }}>
          {notifications.length === 0 && (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Nothing here yet.
              </Typography>
            </Box>
          )}

          {unread.length > 0 && (
            <List
              disablePadding
              subheader={
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  New
                </ListSubheader>
              }
            >
              {unread.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} onSelect={handleSelect} />
              ))}
            </List>
          )}

          {read.length > 0 && (
            <List
              disablePadding
              subheader={
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  Before that
                </ListSubheader>
              }
            >
              {read.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} onSelect={handleSelect} />
              ))}
            </List>
          )}
        </Scrollbar>

        <Divider />

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            disableRipple
            onClick={() => {
              setOpen(false);
              // Same settings screen for both audiences — the backend shows pro
              // staff the pro rows and hides them from homeowners (card 53).
              navigate('/dashboard/user/account');
            }}
          >
            Notification settings
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
